/**
 * MDF-38: Data Library saved views. A view is a named layout of one tab:
 * columns, widths, filters, quick filters, sort and grouping. The cohort
 * selection is not part of a view.
 *
 * This module has no React code, so a node script can test it.
 */
import { mergeColumns, parseColumnState, type StoredColumn } from './columnState';
import { normalizeGroupState } from '../../utils/groupDataByField';
import type { ActiveFilterRow } from '../common/FilterPanel/FilterPanel';
import type { GroupState } from '../common/GroupPanel/GroupPanel';
import type { SortState } from '../../utils/sortTasks';

/** The layout of a tab, in full. */
export interface ViewLayout {
  columns: StoredColumn[];
  widths: Record<string, number>;
  filters: ActiveFilterRow[];
  quickFilters: string[];
  sort: SortState | null;
  group: GroupState | null;
}

/** What the API stores. Every part is optional, so an old view still reads. */
export type ViewBody = Partial<ViewLayout>;

/** The keys the active tab has now. A view can hold keys the tab no longer has. */
export interface ViewKnownKeys {
  filterKeys: string[];
  quickFilterKeys: string[];
  sortFields: string[];
  groupFields: string[];
}

export const VIEW_NAME_MAX = 80;

/** The sort panel fields plus every sortable column, since a header click sorts by the column id. */
export function knownSortFields(panelFields: string[], columns: { id: string; sortable: boolean }[]): string[] {
  return [...new Set([...panelFields, ...columns.filter(c => c.sortable).map(c => c.id)])];
}

/** The body to save for the current layout. Filters with no values are left out. */
export function toViewBody(layout: ViewLayout): ViewBody {
  return {
    columns: layout.columns.map(({ id, visible }) => ({ id, visible })),
    widths: { ...layout.widths },
    filters: layout.filters.filter(f => f.values.length > 0).map(f => ({ key: f.key, values: [...f.values] })),
    quickFilters: [...layout.quickFilters],
    sort: layout.sort ? { field: layout.sort.field, direction: layout.sort.direction } : null,
    group: layout.group ? layout.group.map(({ field, order }) => ({ field, order })) : null,
  };
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The layout that applying a stored view gives. The body is untrusted: a part
 * with the wrong shape, or a column, filter, sort or group field the tab no
 * longer has, is skipped and the rest applies. A part the body does not hold
 * keeps the current value.
 */
export function resolveView(raw: unknown, current: ViewLayout, known: ViewKnownKeys): ViewLayout {
  const body = isObject(raw) ? raw : {};
  const next: ViewLayout = { ...current };

  // parseColumnState already drops columns and widths with the wrong shape.
  const stored = parseColumnState(
    JSON.stringify({ columns: body.columns, widths: isObject(body.widths) ? body.widths : undefined }),
  );
  if (stored.columns) {
    next.columns = mergeColumns(current.columns, stored.columns).map(({ id, visible }) => ({ id, visible }));
  }
  if (stored.widths) next.widths = stored.widths;

  if (Array.isArray(body.filters)) {
    const seen = new Set<string>();
    next.filters = body.filters.flatMap(f => {
      if (!isObject(f) || typeof f.key !== 'string' || !known.filterKeys.includes(f.key) || seen.has(f.key)) return [];
      const values = Array.isArray(f.values) ? f.values.filter((v): v is string => typeof v === 'string') : [];
      if (values.length === 0) return [];
      seen.add(f.key);
      return [{ key: f.key, values }];
    });
  }

  if (Array.isArray(body.quickFilters)) {
    next.quickFilters = [...new Set(body.quickFilters)].filter(
      (k): k is string => typeof k === 'string' && known.quickFilterKeys.includes(k),
    );
  }

  if (body.sort === null) {
    next.sort = null;
  } else if (
    isObject(body.sort) &&
    typeof body.sort.field === 'string' &&
    known.sortFields.includes(body.sort.field) &&
    (body.sort.direction === 'asc' || body.sort.direction === 'desc')
  ) {
    next.sort = { field: body.sort.field, direction: body.sort.direction };
  }

  if ('group' in body) {
    const levels = (normalizeGroupState(body.group) ?? []).filter(l => known.groupFields.includes(l.field));
    next.group = levels.length > 0 ? levels : null;
  }

  return next;
}

/** A comparable form: only visible columns count, and filter and key order does not. */
function signature(layout: ViewLayout): string {
  const visible = layout.columns.filter(c => c.visible).map(c => c.id);
  return JSON.stringify({
    visible,
    widths: visible.filter(id => layout.widths[id] !== undefined).map(id => [id, layout.widths[id]]),
    filters: layout.filters
      .filter(f => f.values.length > 0)
      .map(f => [f.key, [...f.values].sort()])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    quickFilters: [...layout.quickFilters].sort(),
    sort: layout.sort ? [layout.sort.field, layout.sort.direction] : null,
    group: layout.group?.map(l => [l.field, l.order]) ?? null,
  });
}

/** True when the current layout differs from what the view gives. */
export function isLayoutModified(current: ViewLayout, view: ViewLayout): boolean {
  return signature(current) !== signature(view);
}

/** The error to show for a view name, or null when the name is valid. */
export function viewNameError(name: string, takenNames: string[]): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Enter a name';
  if (trimmed.length > VIEW_NAME_MAX) return `Use ${VIEW_NAME_MAX} characters or fewer`;
  if (takenNames.includes(trimmed)) return 'You already have a view with this name';
  return null;
}
